/**
 * Visible "next ammo" meteor at the slingshot origin.
 *
 * The preview meteor sits at the launch zone with the SAME visual scale
 * as the meteor that actually flies — so when fired, the in-flight
 * meteor matches what the player saw and there is no jarring
 * shrink-on-launch.
 *
 * Reload feel: after firing, the preview vanishes for a beat, then the
 * next-tier meteor "drops in" from above with a scale-bounce, mimicking
 * a chamber reload before the player can fire again.
 */

import {
  AdditiveBlending,
  CanvasTexture,
  Color,
  Group,
  Mesh,
  MeshToonMaterial,
  SphereGeometry,
  Sprite,
  SpriteMaterial,
  type Texture,
} from "three";

import { getTier, type PlanetTier } from "../game/tier.ts";
import { planetTexture } from "../render/planet-texture.ts";
import { createToonOutline } from "../render/toon-outline.ts";
import { createToonGradient } from "../shaders/toon-gradient.ts";

/**
 * Visual scale must match the FIRED meteor's outline scale (see
 * planet.ts OUTLINE_SCALE = 1.045) so the player sees the same body
 * before launch and during flight — no jarring shrink at fire time.
 */
const PREVIEW_SCALE = 1.045;
/** Halo size relative to the visual planet radius — bigger than the
 * meteor itself so the launch zone reads from across the playfield. */
const HALO_SCALE = 5.0;

/** Reload animation timing. */
const RELOAD_HIDDEN_MS = 320;     // beat after fire where preview is gone
const RELOAD_DROP_MS = 380;       // "drop in" + bounce duration
const RELOAD_DROP_HEIGHT = 4.5;   // start position above the launch origin

const SHARED_GRADIENT = createToonGradient(4);

export interface LauncherPreviewOptions {
  origin: { x: number; y: number; z: number };
}

export interface LauncherPreview {
  group: Group;
  /** Set the next tier without animation — used for initial mount. */
  setNextTier(tierIndex: number): void;
  /** Trigger reload sequence: hide briefly, then drop the next tier in. */
  reload(tierIndex: number): void;
  setPullOffset(offset: { x: number; y: number }): void;
  setVisible(visible: boolean): void;
  tick(now: number): void;
  detach(): void;
}

function buildHaloTexture(): Texture | null {
  if (typeof document === "undefined") return null;
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const gradient = ctx.createRadialGradient(
    size / 2, size / 2, size * 0.06,
    size / 2, size / 2, size / 2,
  );
  gradient.addColorStop(0, "rgba(255,255,255,1)");
  gradient.addColorStop(0.20, "rgba(255,255,255,0.78)");
  gradient.addColorStop(0.55, "rgba(255,255,255,0.20)");
  gradient.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  ctx.fill();
  return new CanvasTexture(canvas);
}

const SHARED_HALO_TEXTURE = buildHaloTexture();

interface MeteorParts {
  mesh: Mesh;
  outline: Mesh;
  geometry: SphereGeometry;
  material: MeshToonMaterial;
}

function buildMeteor(tier: PlanetTier): MeteorParts {
  const radius = tier.radius * PREVIEW_SCALE;
  const geometry = new SphereGeometry(radius, 40, 28);
  const material = new MeshToonMaterial({
    map: planetTexture(tier.index),
    color: tier.color,
    gradientMap: SHARED_GRADIENT,
  });
  const mesh = new Mesh(geometry, material);
  mesh.name = `launcher-preview-meteor-${tier.name}`;
  const outline = createToonOutline({ planetRadius: radius });
  mesh.add(outline);
  return { mesh, outline, geometry, material };
}

function disposeParts(parts: MeteorParts): void {
  parts.geometry.dispose();
  parts.material.dispose();
  parts.outline.removeFromParent();
  (parts.outline.geometry as SphereGeometry).dispose();
  (parts.outline.material as { dispose(): void }).dispose();
}

interface ReloadState {
  /** When the reload sequence started. */
  startedAt: number;
  /** Tier the reload is bringing in. */
  pendingTier: number;
  /** Have we already swapped meteor mesh to pendingTier? */
  swapped: boolean;
}

export function createLauncherPreview(
  opts: LauncherPreviewOptions,
): LauncherPreview {
  const group = new Group();
  group.name = "launcher-preview";
  group.position.set(opts.origin.x, opts.origin.y, opts.origin.z);

  let currentTier = getTier(0);
  let parts = buildMeteor(currentTier);
  group.add(parts.mesh);

  const haloMaterial = new SpriteMaterial({
    map: SHARED_HALO_TEXTURE ?? undefined,
    color: new Color(currentTier.color),
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
  });
  const halo = new Sprite(haloMaterial);
  halo.name = "launcher-preview-halo";
  let haloBaseSize = currentTier.radius * PREVIEW_SCALE * HALO_SCALE;
  halo.scale.set(haloBaseSize, haloBaseSize, 1);
  group.add(halo);

  let pullX = 0;
  let pullY = 0;
  let reload: ReloadState | null = null;

  const replaceMeteor = (next: PlanetTier): void => {
    parts.mesh.removeFromParent();
    disposeParts(parts);
    parts = buildMeteor(next);
    group.add(parts.mesh);
    haloBaseSize = next.radius * PREVIEW_SCALE * HALO_SCALE;
    halo.scale.set(haloBaseSize, haloBaseSize, 1);
    haloMaterial.color.setHex(next.color);
    currentTier = next;
  };

  const setNextTier = (tierIndex: number): void => {
    if (tierIndex === currentTier.index) return;
    replaceMeteor(getTier(tierIndex));
  };

  const triggerReload = (tierIndex: number): void => {
    reload = {
      startedAt: typeof performance !== "undefined" ? performance.now() : 0,
      pendingTier: tierIndex,
      swapped: tierIndex === currentTier.index,
    };
    // Hide immediately — meteor "left the chamber".
    parts.mesh.visible = false;
    halo.visible = false;
  };

  const setPullOffset = (offset: { x: number; y: number }): void => {
    pullX = offset.x;
    pullY = offset.y;
    if (reload) return; // pull offsets ignored during reload
    group.position.set(
      opts.origin.x + pullX,
      opts.origin.y + pullY,
      opts.origin.z,
    );
  };

  const setVisible = (visible: boolean): void => {
    group.visible = visible;
  };

  const tick = (now: number): void => {
    const t = now / 1000;

    if (reload) {
      const elapsed = now - reload.startedAt;
      if (elapsed < RELOAD_HIDDEN_MS) {
        // Empty chamber phase — nothing visible.
        parts.mesh.visible = false;
        halo.visible = false;
        return;
      }
      // Drop-in phase. Swap meteor mesh once if the next tier differs.
      if (!reload.swapped) {
        replaceMeteor(getTier(reload.pendingTier));
        reload.swapped = true;
      }
      const dropElapsed = elapsed - RELOAD_HIDDEN_MS;
      const k = Math.min(1, dropElapsed / RELOAD_DROP_MS);
      // Ease-out cubic for the position drop.
      const easePos = 1 - Math.pow(1 - k, 3);
      const yDrop = (1 - easePos) * RELOAD_DROP_HEIGHT;
      // Bounce-easing scale — overshoots 1 then settles.
      const overshoot = 1.7;
      const easeScale =
        1 + overshoot * Math.pow(k - 1, 3) +
        (overshoot - 0) * Math.pow(k - 1, 2);
      // easeScale starts ≈ 0 at k=0 and lands at 1 at k=1.
      parts.mesh.scale.set(easeScale, easeScale, easeScale);
      parts.mesh.position.y = yDrop;
      parts.mesh.visible = true;
      halo.visible = true;
      haloMaterial.opacity = Math.min(0.8, k * 0.9);
      if (k >= 1) {
        parts.mesh.scale.set(1, 1, 1);
        parts.mesh.position.y = 0;
        reload = null;
      }
      // Group still respects pull offset once reload finishes — apply
      // it again so the pre-load state matches.
      group.position.set(opts.origin.x, opts.origin.y, opts.origin.z);
      return;
    }

    // Idle pulse + slow self-spin while the chamber is loaded.
    const pulse = 1 + Math.sin(t * 3) * 0.10;
    halo.scale.set(haloBaseSize * pulse, haloBaseSize * pulse, 1);
    haloMaterial.opacity = 0.65 + Math.sin(t * 3) * 0.10;
    parts.mesh.rotation.y = t * 0.6;
    parts.mesh.position.y = Math.sin(t * 1.6) * 0.08;
    group.position.set(
      opts.origin.x + pullX,
      opts.origin.y + pullY,
      opts.origin.z,
    );
  };

  const detach = (): void => {
    parts.mesh.removeFromParent();
    disposeParts(parts);
    halo.removeFromParent();
    haloMaterial.dispose();
    group.removeFromParent();
  };

  return {
    group,
    setNextTier,
    reload: triggerReload,
    setPullOffset,
    setVisible,
    tick,
    detach,
  };
}

export const __testing = {
  PREVIEW_SCALE,
  HALO_SCALE,
  RELOAD_HIDDEN_MS,
  RELOAD_DROP_MS,
};
